import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { toast } from 'sonner';
import {
  EnvelopeSimple, Bell, Gear, Play, Plus, Trash,
  Clock, CheckCircle, X, ChatTeardropText, ArrowClockwise
} from '@phosphor-icons/react';

export default function DigestPage() {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showAddChannel, setShowAddChannel] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, historyRes] = await Promise.all([
        fetch(`${API}/digest/config`, { credentials: 'include' }),
        fetch(`${API}/digest/history`, { credentials: 'include' }),
      ]);
      if (configRes.ok) setConfig(await configRes.json());
      if (historyRes.ok) setHistory(await historyRes.json());
    } catch (e) { console.error('Digest fetch error:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleActive = async () => {
    const res = await fetch(`${API}/digest/config`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !config?.is_active }),
    });
    if (res.ok) { toast.success(config?.is_active ? 'Digest paused' : 'Digest activated'); fetchData(); }
  };

  const handlePreview = async () => {
    const res = await fetch(`${API}/digest/preview`, { credentials: 'include' });
    if (res.ok) setPreview(await res.json());
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/digest/send`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        const succeeded = d.results?.filter(r => r.success).length || 0;
        toast.success(`Digest sent to ${succeeded} channel(s)`);
        fetchData();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Send failed');
      }
    } catch { toast.error('Send failed'); }
    setSending(false);
  };

  const handleDeleteChannel = async (channelId) => {
    const res = await fetch(`${API}/digest/channels/${channelId}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) { toast.success('Channel removed'); fetchData(); }
  };

  if (loading) return <div className="text-center py-12 text-kova-slate font-mono text-xs">Loading digest config...</div>;

  return (
    <div className="space-y-6" data-testid="digest-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-kova-white tracking-tight">Daily Digest</h1>
          <p className="font-mono text-[10px] tracking-[2px] uppercase text-kova-slate mt-1">
            Slack & Email Activity Summary
            {config?.is_active && (
              <span className="ml-3 inline-block border border-kova-green/20 bg-kova-green/10 text-kova-green px-1.5 py-0.5">ACTIVE</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handlePreview} data-testid="preview-btn" className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase text-kova-silv border border-kova-cyan/12 px-3 py-2 hover:border-kova-cyan/25 hover:text-kova-white transition-colors">
            <Bell size={14} /> Preview
          </button>
          <button onClick={handleSendNow} disabled={sending || !config?.channels?.length} data-testid="send-now-btn" className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 transition-colors disabled:opacity-50 cursor-crosshair">
            {sending ? <ArrowClockwise size={14} className="animate-spin" /> : <Play size={14} weight="bold" />}
            {sending ? 'Sending...' : 'Send Now'}
          </button>
        </div>
      </div>

      {/* Config Card */}
      <div className="bg-kova-card border border-kova-cyan/12 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate">Schedule & Settings</h3>
          <button onClick={handleToggleActive} data-testid="toggle-active" className={`w-10 h-5 rounded-full transition-colors ${config?.is_active ? 'bg-kova-cyan' : 'bg-kova-card2 border border-kova-cyan/12'}`}>
            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${config?.is_active ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="font-mono text-[9px] text-kova-slate uppercase">Delivery Hour (UTC)</p>
            <p className="font-heading text-lg font-bold text-kova-white">{config?.hour_utc ?? 8}:00</p>
          </div>
          <div>
            <p className="font-mono text-[9px] text-kova-slate uppercase">Channels</p>
            <p className="font-heading text-lg font-bold text-kova-white">{config?.channels?.length || 0}</p>
          </div>
          <div>
            <p className="font-mono text-[9px] text-kova-slate uppercase">Includes</p>
            <div className="flex gap-1 mt-1 flex-wrap">
              {config?.include_alerts && <span className="font-mono text-[8px] bg-kova-cyan/10 text-kova-cyan px-1 py-0.5">Alerts</span>}
              {config?.include_signals && <span className="font-mono text-[8px] bg-kova-purple/10 text-kova-purple px-1 py-0.5">Signals</span>}
              {config?.include_commons && <span className="font-mono text-[8px] bg-kova-green/10 text-kova-green px-1 py-0.5">Commons</span>}
              {config?.include_schema && <span className="font-mono text-[8px] bg-kova-amber/10 text-kova-amber px-1 py-0.5">Schema</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Channels */}
      <div className="bg-kova-card border border-kova-cyan/12">
        <div className="px-5 py-3 border-b border-kova-cyan/12 flex items-center justify-between">
          <span className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate">Distribution Channels</span>
          <button onClick={() => setShowAddChannel(true)} data-testid="add-channel-btn" className="flex items-center gap-1 font-mono text-[9px] tracking-[2px] uppercase text-kova-cyan hover:text-kova-cyan2">
            <Plus size={12} /> Add
          </button>
        </div>
        {(!config?.channels || config.channels.length === 0) ? (
          <div className="p-8 text-center">
            <EnvelopeSimple size={24} className="mx-auto mb-2 text-kova-slate" />
            <p className="text-kova-silv text-xs">No channels configured</p>
            <p className="text-kova-slate text-[10px] mt-1">Add a Slack webhook or email to receive digests</p>
          </div>
        ) : (
          <div>
            {config.channels.map(ch => (
              <div key={ch.channel_id} className="px-5 py-3 border-b border-kova-cyan/6 flex items-center justify-between group">
                <div className="flex items-center gap-3">
                  {ch.type === 'slack' ? <ChatTeardropText size={16} className="text-kova-purple" /> : <EnvelopeSimple size={16} className="text-kova-cyan" />}
                  <div>
                    <span className="font-mono text-[11px] text-kova-white">{ch.label || ch.target}</span>
                    <span className="font-mono text-[9px] text-kova-slate ml-2 uppercase">{ch.type}</span>
                  </div>
                </div>
                <button onClick={() => handleDeleteChannel(ch.channel_id)} className="p-1 text-kova-slate hover:text-kova-red opacity-0 group-hover:opacity-100 transition-all">
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview */}
      {preview && (
        <div className="bg-kova-card border border-kova-cyan/12 p-5" data-testid="digest-preview">
          <h3 className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mb-3">Digest Preview</h3>
          <pre className="font-mono text-xs text-kova-silv whitespace-pre-wrap leading-relaxed">{preview.text}</pre>
        </div>
      )}

      {/* History */}
      <div className="bg-kova-card border border-kova-cyan/12">
        <div className="px-5 py-3 border-b border-kova-cyan/12">
          <span className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate">Send History</span>
        </div>
        {history.length === 0 ? (
          <div className="p-8 text-center text-kova-slate font-mono text-xs">No digests sent yet</div>
        ) : (
          <div>
            {history.map((h, i) => (
              <div key={i} className="px-5 py-2.5 border-b border-kova-cyan/6 flex items-center justify-between font-mono text-xs">
                <div className="flex items-center gap-2">
                  <Clock size={12} className="text-kova-slate" />
                  <span className="text-kova-silv">{new Date(h.sent_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-kova-green">{h.channels_succeeded}/{h.channels_attempted} sent</span>
                  <span className="text-kova-slate">{h.data_summary?.alerts || 0} alerts, {h.data_summary?.executions || 0} runs</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Channel Modal */}
      {showAddChannel && <AddChannelModal onClose={() => setShowAddChannel(false)} onSuccess={() => { setShowAddChannel(false); fetchData(); }} />}
    </div>
  );
}

function AddChannelModal({ onClose, onSuccess }) {
  const [type, setType] = useState('email');
  const [target, setTarget] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await fetch(`${API}/digest/channels`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, target, label: label || target.slice(0, 30) }),
    });
    if (res.ok) { toast.success('Channel added'); onSuccess(); }
    else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-kova-card border border-kova-cyan/12 p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="add-channel-modal">
        <h2 className="font-heading text-lg font-bold text-kova-white mb-4">Add Channel</h2>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Type</label>
            <select data-testid="channel-type" value={type} onChange={e => setType(e.target.value)} className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2">
              <option value="email">Email</option>
              <option value="slack">Slack Webhook</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">{type === 'email' ? 'Email Address' : 'Webhook URL'}</label>
            <input data-testid="channel-target" type="text" required value={target} onChange={e => setTarget(e.target.value)} placeholder={type === 'email' ? 'team@company.com' : 'https://hooks.slack.com/...'} className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan" />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Label (optional)</label>
            <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Engineering Team" className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="font-mono text-[10px] tracking-[2px] uppercase text-kova-silv border border-kova-cyan/12 px-4 py-2">Cancel</button>
            <button data-testid="channel-save" type="submit" disabled={saving} className="font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 disabled:opacity-50">{saving ? 'Saving...' : 'Add Channel'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
